/**
 * Wire contracts between the web app and the API.
 *
 * Every one of these crosses a trust boundary. The server re-parses and
 * re-validates regardless of what the client sent — client-side parsing is a
 * UX affordance and is never the source of truth. SPEC-0001 step 4.
 */
import { z } from 'zod';
import { columnMappingSchema, roleAliasSchema } from './canonical.js';
/**
 * How to treat a repeated `(project_id, role)` pair.
 *
 * RESOLVED — SPEC-0001 #1 (2026-10-16): no fixed default, permanently. It
 * travels on the request, and is recorded on the run, precisely so that no
 * layer of this system quietly decides it. See `DuplicatePolicy` in
 * `@picklii/recon`.
 */
export const duplicatePolicySchema = z.enum(['sum', 'latest', 'reject']);
export const createRunRequestSchema = z.object({
    soldMapping: columnMappingSchema,
    budgetMapping: columnMappingSchema,
    aliases: z.array(roleAliasSchema).default([]),
    duplicatePolicy: duplicatePolicySchema,
    /**
     * Which saved mapping each side used, by name, for the record.
     *
     * Purely descriptive: saving a profile is its own explicit action against
     * `/api/profiles`, not a side effect of reconciling. Creating records here
     * would mean a run could quietly mint a profile, which is the kind of
     * implicit write that makes a system hard to reason about.
     */
    usedProfiles: z
        .object({ sold: z.string().trim().min(1).optional(), budget: z.string().trim().min(1).optional() })
        .optional(),
});
/**
 * Set a project's lifecycle state or dates directly. SPEC-0007. This is the
 * one path that overrides ingest-derived values — normally used to clear a
 * flagged `dateMismatches` entry, or to archive/unarchive.
 */
export const updateProjectRequestSchema = z
    .object({
    archived: z.boolean().optional(),
    goLiveDate: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
})
    .refine((v) => v.archived !== undefined || v.goLiveDate !== undefined || v.endDate !== undefined, {
    message: 'At least one field must be set.',
});
export const BAND_ORDER = ['Critical', 'High', 'Medium', 'Low'];
//# sourceMappingURL=api.js.map